import Markdown from "markdown-to-jsx";
import React, { ReactNode } from "react";
import { SmartBannerBlueprint } from "./BannerFacilitator";
import type {
    ChatSearchResult,
    TranscriptionSearchResult,
} from "components/Evaluation/Stores/EvalStore";
import { Blurb } from "components/UI/AcxTranscription/Components/BlurbBox/Blurb";
import { WordsAndPhrasesBlurb } from "./SmartBannerBlurbs/WordsAndPhrasesBlurb";


export function buildSmartBanner(
    blueprint: SmartBannerBlueprint | null,
): ReactNode {
    if (!blueprint) return null;
    let message: string;
    // Build the banner based on the blueprint type and business logic
    switch (blueprint.type) {
        case "conversationExplorerFilters":
            message = blueprint.smartBannerMessage;
            return <Markdown>{message}</Markdown>;
        case "focusConversationFilters":
            message = blueprint.smartBannerMessage
                .replace(/Eddy Effect/g, (match) => `<EddyShortcut />`)
                .replace(/safety event/g, (match) => `<SafetyEventShortcut />`)
                .replace(
                    /words and phrases/g,
                    (match) => `<WordsAndPhrasesCollapse />`,
                );

            const containsWordsAndPhrases =
                blueprint.searchResults &&
                blueprint.searchResults.length > 0 &&
                blueprint.speakerStore;

            return (
                <div style={{ width: "100%" }}>
                    <Markdown
                        options={{
                            overrides: {
                                EddyShortcut: {
                                    component: () => (
                                        <span
                                            style={{
                                                textDecoration: "underline",
                                                cursor: "pointer",
                                            }}
                                            id="smart-banner-conversation-details-eddy-effect"
                                            onClick={() => {
                                                document
                                                    .getElementById(
                                                        "conversation-summary-eddy-effect",
                                                    )
                                                    ?.scrollIntoView({
                                                        behavior: "smooth",
                                                    });
                                            }}
                                        >
                                            Eddy Effect
                                        </span>
                                    ),
                                },
                                SafetyEventShortcut: {
                                    component: () => (
                                        <span
                                            style={{
                                                textDecoration: "underline",
                                                cursor: "pointer",
                                            }}
                                            id="smart-banner-conversation-details-safety-event"
                                            onClick={() =>
                                                document
                                                    .getElementById(
                                                        "conversation-summary-safety-event",
                                                    )
                                                    ?.scrollIntoView({
                                                        behavior: "smooth",
                                                    })
                                            }
                                        >
                                            Safety Event
                                        </span>
                                    ),
                                },
                                WordsAndPhrasesCollapse: {
                                    component: () => {
                                        return (
                                            <span
                                                style={{
                                                    fontWeight: "bold",
                                                    color: "#3364D6",
                                                    textDecoration:
                                                        containsWordsAndPhrases
                                                            ? "underline"
                                                            : "none",
                                                    cursor: containsWordsAndPhrases
                                                        ? "pointer"
                                                        : "default",
                                                }}
                                                id="smart-banner-conversation-details-words-and-phrases"
                                                onClick={() => {
                                                    if (
                                                        containsWordsAndPhrases
                                                    ) {
                                                        const el =
                                                            document.getElementById(
                                                                "words-and-phrases-collapse",
                                                            );
                                                        if (el) {
                                                            if (
                                                                el.style
                                                                    .display ===
                                                                "none"
                                                            ) {
                                                                el.style.display =
                                                                    "flex";
                                                            } else {
                                                                el.style.display =
                                                                    "none";
                                                            }
                                                        }
                                                    }
                                                }}
                                            >
                                                words and phrases
                                            </span>
                                        );
                                    },
                                },
                            },
                        }}
                    >
                        {message}
                    </Markdown>
                    {containsWordsAndPhrases && (
                        <div
                            style={{
                                width: "100%",
                                gap: "4px",
                                flexDirection: "column",
                                marginTop: "8px",
                                display: "none",
                            }}
                            id="words-and-phrases-collapse"
                        >
                            {blueprint.searchResults &&
                                blueprint.searchResults
                                    .sort(() => Math.random() - 0.5)
                                    .slice(0, 4)
                                    .sort((a, b) => {
                                        const aIsCall =
                                            typeof a === "object" &&
                                            "blurb" in a;
                                        const bIsCall =
                                            typeof b === "object" &&
                                            "blurb" in b;
                                        if (aIsCall && bIsCall) {
                                            const aCall = a as TranscriptionSearchResult;
                                            const bCall = b as TranscriptionSearchResult;
                                            if (
                                                aCall.phraseStartTime !==
                                                    undefined &&
                                                bCall.phraseStartTime !==
                                                    undefined
                                            ) {
                                                return (
                                                    aCall.phraseStartTime -
                                                    bCall.phraseStartTime
                                                );
                                            } else {
                                                return 0;
                                            }
                                        }
                                        else {
                                            const aChat = a as ChatSearchResult;
                                            const bChat = b as ChatSearchResult;
                                            if (
                                                aChat.timestamp < bChat.timestamp
                                            ) {
                                                return -1;
                                            }
                                            if (
                                                aChat.timestamp > bChat.timestamp
                                            ) {
                                                return 1;
                                            }
                                            return 0;
                                        }
                                    })
                                    .map((searchResult, i) => (
                                        <WordsAndPhrasesBlurb
                                            key={i}
                                            searchResult={searchResult}
                                            onClick={(searchResult) => {
                                                if (
                                                    typeof searchResult ===
                                                        "object" &&
                                                    "blurb" in searchResult
                                                ) {
                                                    // This is a call search result
                                                    const transcriptionSearchResult =
                                                        searchResult as TranscriptionSearchResult;
                                                    if (
                                                        transcriptionSearchResult.phraseStartTime ===
                                                        undefined
                                                    ) {
                                                        return;
                                                    }
                                                    const blurb =
                                                        transcriptionSearchResult.blurb;
                                                    if (
                                                        blurb &&
                                                        "timeInterval" in blurb
                                                    ) {
                                                        // Handle transcript blurb click
                                                        const transcriptBlurb =
                                                            blurb as Blurb;
                                                        if (
                                                            blueprint.transcriptionStore
                                                        ) {
                                                            const index =
                                                                blueprint.transcriptionStore.blurbs.findIndex(
                                                                    (b) =>
                                                                        b
                                                                            .timeInterval[0] ===
                                                                        transcriptBlurb
                                                                            .timeInterval[0],
                                                                );
                                                            blueprint.transcriptionStore.setScrollIndex(
                                                                index,
                                                            );
                                                            blueprint.onClick();
                                                        }
                                                    }
                                                }
                                            }}
                                            speakerStore={
                                                blueprint.speakerStore!
                                            }
                                            searchItems={blueprint.searchItems}
                                        />
                                    ))}
                        </div>
                    )}
                </div>
            );
        default:
            console.error(
                `No builder found for Smart Banner Blueprint type: ${blueprint.type}`,
            );
            return null;
    }
}
